import React from 'react';
import { Hammer, Cpu, Flame, ClipboardCheck } from 'lucide-react';
import { servicesData } from '../mockData';

const iconMap = {
  1: Hammer,
  2: Cpu,
  3: Flame,
  4: ClipboardCheck
};

const images = {
  1: '/kitchen.jpg',
  2: '/living.jpg',
  3: '/bedroom.jpg',
  4: '/bedroom2.jpg'
};

export const ServicesSection = () => {
  return (
    <section id="services" className="py-24 bg-black relative overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#FF6B00 1px, transparent 1px), linear-gradient(90deg, #FF6B00 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite'
        }}></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-40 w-3 h-3 bg-white rounded-full animate-pulse delay-100"></div>
        <div className="absolute bottom-40 left-60 w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse delay-200"></div>
        <div className="absolute bottom-60 right-32 w-3 h-3 bg-white rounded-full animate-pulse delay-300"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Title with 3D Effect */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#FF6B00]/10 backdrop-blur-md rounded-full border border-[#FF6B00]/30 mb-6 animate-fade-in-down">
            <div className="w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse"></div>
            <span className="text-[#FF6B00] uppercase tracking-widest text-sm font-bold">What We Offer</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6 animate-fade-in-up animation-delay-200">
            Our Premium <span className="bg-gradient-to-r from-[#FF6B00] to-[#FFB366] bg-clip-text text-transparent">Services</span>
          </h2>
          
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed animate-fade-in-up animation-delay-400">
            Comprehensive turnkey solutions tailored to transform your vision into reality
          </p>
        </div>

        {/* Services Grid with 3D Card Flip Effect */}
        <div className="grid md:grid-cols-2 gap-10">
          {servicesData.map((service, index) => {
            const Icon = iconMap[service.id];
            const bgImage = images[service.id];
            return (
              <div
                key={service.id}
                className="service-card-3d h-[450px]"
                style={{ 
                  animationDelay: `${index * 0.2}s`,
                  perspective: '1000px'
                }}
              >
                <div className="service-card-inner relative w-full h-full transition-transform duration-700 preserve-3d">
                  {/* Front Face */}
                  <div className="service-card-face absolute inset-0 w-full h-full backface-hidden rounded-3xl overflow-hidden shadow-2xl">
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${bgImage})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"></div>
                    </div>
                    
                    <div className="relative h-full flex flex-col justify-end p-8 text-white">
                      {/* Icon with 3D Effect */}
                      <div className="mb-6 transform-gpu">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#FF6B00] to-[#E65F00] rounded-2xl flex items-center justify-center shadow-lg shadow-[#FF6B00]/50 transform hover:scale-110 hover:rotate-6 transition-all duration-300">
                          <Icon className="text-white" size={36} />
                        </div>
                      </div>
                      
                      <h3 className="text-3xl md:text-4xl font-black mb-4 text-white">
                        {service.title}
                      </h3>
                      
                      <p className="text-gray-300 text-lg leading-relaxed">
                        {service.description}
                      </p>

                      {/* Hover Indicator */}
                      <div className="mt-6 flex items-center gap-2 text-[#FF6B00] font-semibold">
                        <span className="text-sm uppercase tracking-wider">View Details</span>
                        <div className="w-8 h-px bg-[#FF6B00]"></div>
                      </div>
                    </div>
                  </div>

                  {/* Back Face */}
                  <div className="service-card-face service-card-back absolute inset-0 w-full h-full backface-hidden rounded-3xl overflow-hidden bg-gradient-to-br from-[#FF6B00] via-[#E65F00] to-[#CC5500] shadow-2xl rotate-y-180">
                    <div className="relative h-full p-8 flex flex-col justify-center text-white">
                      <div className="mb-6">
                        <Icon className="text-white/90" size={48} />
                      </div>
                      
                      <h3 className="text-3xl font-black mb-6">{service.title}</h3>
                      
                      <ul className="space-y-4 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-white/90">
                            <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-base leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Back Indicator */}
                      <div className="mt-auto flex items-center gap-2 text-white/80 text-sm">
                        <span className="uppercase tracking-wider">Hover to return</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        @keyframes gridMove {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }

        .service-card-3d {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .service-card-inner {
          transform-style: preserve-3d;
        }

        .service-card-3d:hover .service-card-inner {
          transform: rotateY(180deg);
        }

        .service-card-face {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .service-card-back {
          transform: rotateY(180deg);
        }

        .preserve-3d {
          transform-style: preserve-3d;
        }

        .backface-hidden {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </section>
  );
};

